import React, { useCallback, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { FeatureData } from "../types";
import ChecklistItem from "./ChecklistItem";
import "./FeatureCard.css";

interface FeatureCardProps {
  feature: FeatureData;
  checked: Record<string, boolean>;
  onToggleItem: (itemId: string) => void;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  feature,
  checked,
  onToggleItem,
}) => {
  const [expanded, setExpanded] = useState(false);

  const checkedCount = useMemo(
    () => feature.items.filter((item) => checked[item.id]).length,
    [feature.items, checked],
  );

  const isComplete = checkedCount === feature.items.length;

  const handleToggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  return (
    <div
      className={`feature-card${isComplete ? " feature-card--complete" : ""}`}
    >
      <button
        className="feature-card__header"
        type="button"
        onClick={handleToggleExpanded}
      >
        <div className="feature-card__header-text">
          <p className="feature-card__title">{feature.title}</p>
          <p className="feature-card__description">{feature.description}</p>
        </div>
        <div className="feature-card__meta">
          <span className="feature-card__count">
            {checkedCount} / {feature.items.length}
          </span>
          <ChevronDown
            size={18}
            className={`feature-card__chevron${expanded ? " feature-card__chevron--open" : ""}`}
          />
        </div>
      </button>

      {expanded && (
        <div className="feature-card__body">
          <div className="feature-card__items">
            {feature.items.map((item) => (
              <ChecklistItem
                key={item.id}
                item={item}
                checked={!!checked[item.id]}
                onToggle={onToggleItem}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureCard;
